import type React from "react";
import z from "zod";

export type PropState<T> = [T, React.Dispatch<React.SetStateAction<T>>];

export const UserSchema = z.object({
  object: z.literal("user"),
  id: z.string(),
  name: z.string(),
  email: z.string().email(),
  picture: z.string(),
});

export const OrgUserRoleSchema = z.union([
  z.literal("reader"),
  z.literal("developer"),
  z.literal("owner"),
]);

export const OrgUserSchema = z.object({
  object: z.literal("organization_user"),
  role: OrgUserRoleSchema,
  created: z.number(),
  user: UserSchema,
});

export const OrgVerificationStatus = z.union([
  z.literal("VERIFIED"),
  z.literal("RESTRICTED"),
  z.literal("SUSPICIOUS"),
]);

export const OrgTagSchema = z.object({
  object: z.literal("org_tag"),
  id: z.string(),
  org_id: z.string(),
  tag: z.string(),
  created_at: z.number(),
});

export const OrganizationSchema = z.object({
  object: z.literal("organization"),
  id: z.string(),
  created: z.number(),
  name: z.string(),
  description: z.string(),
  personal: z.boolean(),
  settings: z.record(z.unknown()),
  role: OrgUserRoleSchema,
  stripe_customer_id: z.string().optional(),
  billing_plan: z.string().optional(),
  billing_subscription_id: z.string().optional(),
  allowed_plan: z.string().optional(),
  verification_status: OrgVerificationStatus.optional(),
  org_tags: z
    .object({
      object: z.literal("list"),
      data: z.array(OrgTagSchema),
    })
    .optional(),
});

export const UserInviteSchema = z.object({
  email: z.string().email(),
  id: z.string(),
  is_expired: z.boolean(),
  role: z.string(),
  token: z.string().optional(),
});

export const ProfileProjectSchema = z.object({
  object: z.literal("project"),
  id: z.string(),
  name: z.string(),
  org_id: z.string(),
  created_at: z.number(),
  updated_at: z.number(),
  deleted_at: z.number().nullable(),
});

export const ProfileOrgSchema = z.object({
  object: z.literal("organization"),
  id: z.string(),
  created: z.number(),
  name: z.string(),
  description: z.string(),
  personal: z.boolean(),
  verification_status: OrgVerificationStatus.optional(),
  vercel_managed: z.boolean().optional(),
  billing_plan: z.string().optional(),
  allowed_plan: z.string().optional(),
  stripe_customer_id: z.string().optional(),
  billing_subscription_id: z.string().optional(),
  settings: z.record(z.unknown()),
  role: OrgUserRoleSchema,
  tos_approved_at: z.number().nullable(),
  tos_approved_by: z.string().nullable(),
  projects: z.object({
    data: z.array(ProfileProjectSchema),
  }),
  org_tags: z.object({
    object: z.literal("list"),
    data: z.array(OrgTagSchema),
  }),
});

export const ProfileSchema = z.object({
  user: z.object({
    object: z.literal("user"),
    id: z.string(),
    name: z.string(),
    email: z.string(),
    picture: z.string(),
    created: z.number(),
    opt_into_newsletter: z.number(),
    intercom_hash: z.string(),
    auth: z.object({
      provider: z.string(),
      provider_id: z.string(),
    }),
    orgs: z.object({
      object: z.literal("list"),
      data: z.array(ProfileOrgSchema),
    }),
  }),
});

export type Profile = z.infer<typeof ProfileSchema>;
export type ProfileOrg = z.infer<typeof ProfileOrgSchema>;
export type ProfileProject = z.infer<typeof ProfileProjectSchema>;
export const OrgUserRoles = OrgUserRoleSchema._def.options.map((o) => o.value);
export type OrgUserRole = z.infer<typeof OrgUserRoleSchema>;
export type User = z.infer<typeof UserSchema>;
export type OrgUser = z.infer<typeof OrgUserSchema>;
export type Organization = z.infer<typeof OrganizationSchema>;
export type UserInvite = z.infer<typeof UserInviteSchema>;
export type BillingTiers = "free" | "developer" | "enterprise";

export function billingPlanToTiers(plan: string): BillingTiers {
  if (plan.startsWith("developer")) {
    return "developer";
  }
  if (plan.startsWith("enterprise")) {
    return "enterprise";
  }
  return "free";
}

export function getUpgradablePlan(
  org?: Organization,
): BillingTiers | undefined {
  if (!org) {
    return;
  }

  if (org.allowed_plan) {
    return billingPlanToTiers(org.allowed_plan);
  }

  return;
}

export function getActiveTier(org?: Organization): BillingTiers | undefined {
  if (!org) {
    return;
  }
  if (org.billing_plan == null) {
    return "free";
  }
  const tier = billingPlanToTiers(org.billing_plan);
  return tier;
}

export function billingTierToLabel(plan: BillingTiers): string {
  switch (plan) {
    case "free":
      return "Free";
    case "developer":
      return "Developer";
    case "enterprise":
      return "Business";
    default:
      return "Unknown";
  }
}
